<!--
  COD5 · Transazioni in app — resoconto mensile di un partner.

  Stesso impianto di COD6, COD7 e COD8 (mese, KPI, segmenti, tabella).

  L'id della rotta è quello del **partner** (non della sede): il resoconto è
  dell'intero partner, con dentro tutte le sue sedi.

  COME SI RICAVA IL PERIODO. `task` non ha alcun campo data: qui il periodo
  si ottiene passando dal
  booking a cui il task è collegato (`booking.data`): si caricano i booking del
  partner nel mese con i loro task, e le transazioni si estraggono da lì.
  Una riga della tabella = un task, cioè una transazione.

  ⚠️ IL COD5 NON MUOVE IL MARGINE. I soldi che il cliente paga in app finiscono
  sul conto del partner, non su quello di Simba: qui si guarda, non si liquida.
  Per questo non c'è nessuna azione di chiusura, a differenza di COD7 e COD8.
-->
<template>
  <div
    v-loading="loading"
    class="c5"
  >
    <div class="c5__head">
      <button
        type="button"
        class="c5__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c5__title">
          <span class="c5__code">COD5</span> Transazioni in app
        </h1>
        <p class="c5__sub">
          {{ partnerName || 'Partner' }} — incassi dei clienti tramite app
        </p>
      </div>
    </div>

    <div class="c5__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="c5__sp" />
      <el-input
        v-model="search"
        placeholder="Cliente o descrizione…"
        clearable
        style="width: 240px"
      />
    </div>

    <div
      v-if="truncated"
      class="warnbox"
    >
      <span class="warnbox__ico">⚠</span>
      <div class="warnbox__txt">
        <b>Elenco incompleto.</b>
        Il backend ha restituito {{ rowsCount }} booking su {{ declaredTotal }}
        dichiarati: i totali qui sotto sarebbero parziali. Restringi il periodo.
      </div>
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__lbl">
          Incassato in app
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.paid) }}
        </div>
      </div>
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Non ancora pagato
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.unpaid) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Transazioni
        </div>
        <div class="kpi__num">
          {{ transactions.length }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Booking coinvolti
        </div>
        <div class="kpi__num">
          {{ kpi.bookings }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="segment = s.key"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        :data="filtered"
        style="width: 100%"
        empty-text="Nessuna transazione in app per il periodo selezionato"
      >
        <el-table-column
          label="Data"
          width="120"
        >
          <template #default="scope">
            {{ fmtDate(scope.row.date) }}
          </template>
        </el-table-column>

        <el-table-column
          label="Cliente"
          min-width="200"
        >
          <template #default="scope">
            <div class="cli__nm">
              {{ scope.row.customer || '—' }}
            </div>
            <div class="cli__sub">
              {{ scope.row.headquarter || '' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Descrizione"
          min-width="240"
        >
          <template #default="scope">
            {{ scope.row.description || '—' }}
          </template>
        </el-table-column>

        <el-table-column
          label="Booking"
          width="130"
        >
          <template #default="scope">
            <button
              type="button"
              class="lnk"
              @click="openBooking(scope.row.bookingId)"
            >
              {{ shortId(scope.row.bookingId) }}
            </button>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="140"
        >
          <template #default="scope">
            <!-- ⚠️ Pagato = `accepted` ("impostare che il cliente ha
                 pagato", booking.service.ts). `pay` dice solo che la voce è
                 pagabile in app: leggerlo come incasso faceva risultare
                 saldato tutto ciò che era semplicemente da saldare. -->
            <StatusPill
              :kind="scope.row.paid ? 'ok' : 'attn'"
              :label="scope.row.paid ? 'Pagato' : 'Da pagare'"
            />
            <div
              v-if="!scope.row.inApp"
              class="c5__meta"
            >
              non pagabile in app
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Importo"
          width="130"
          align="right"
        >
          <template #default="scope">
            <span class="val">{{ euro(scope.row.cost) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="board__foot">
        <span>{{ filtered.length }} transazioni</span>
        <span class="c5__sp" />
        <span class="board__tot">
          Totale mostrato <b>{{ euro(filteredTotal) }}</b>
        </span>
      </div>
    </div>

    <p class="c5__note">
      I pagamenti in app finiscono sul conto del <b>partner</b>, non su quello
      di Simba: questo codice è di sola lettura e non muove il margine. A Simba
      resta la fatturazione del booking, che sta nel COD3.
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import partnerAPI from '../../../services/partnerAPI.js';
import StatusPill from '../../../components/ui/StatusPill.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'all', label: 'Tutte' },
  { key: 'paid', label: 'Pagate' },
  { key: 'unpaid', label: 'Non pagate' },
];

export default {
  name: 'Cod5Page',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const bookings = ref([]);
    const truncated = ref(false);
    const declaredTotal = ref(0);
    const loading = ref(false);
    const search = ref('');
    const segment = ref('all');
    const partnerName = ref('');

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);
    const rowsCount = computed(() => bookings.value.length);

    // Una riga = un task. Il booking porta la data, il cliente e la sede.
    const transactions = computed(() => {
      const out = [];
      for (const b of bookings.value) {
        for (const t of b.tasks ?? []) {
          out.push({
            id: t.id,
            bookingId: b.id,
            date: b.data,
            customer: [b.customer?.name, b.customer?.surname].filter(Boolean).join(' '),
            headquarter: b.headquarter?.name || b.headquarter?.city || '',
            description: t.description,
            cost: Number(t.cost || 0),
            // `accepted` = saldato · `pay` = pagabile in app
            paid: !!t.accepted,
            inApp: !!t.pay,
          });
        }
      }
      return out.sort((a, z) => new Date(a.date) - new Date(z.date));
    });

    const bySegment = (key) =>
      transactions.value.filter((t) => {
        if (key === 'paid') return t.paid;
        if (key === 'unpaid') return !t.paid;
        return true;
      });

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter(
        (t) => !q || `${t.customer} ${t.description}`.toLowerCase().includes(q)
      );
    });

    const filteredTotal = computed(() =>
      filtered.value.reduce((s, t) => s + t.cost, 0)
    );

    const kpi = computed(() => {
      let paid = 0;
      let unpaid = 0;
      const withTask = new Set();
      for (const t of transactions.value) {
        if (t.paid) paid += t.cost;
        else unpaid += t.cost;
        withTask.add(t.bookingId);
      }
      return { paid, unpaid, bookings: withTask.size };
    });

    const load = async () => {
      loading.value = true;
      try {
        const res = await contabilitaAPI.cod5Rows(
          route.params.id, year.value, month.value
        );
        bookings.value = res.bookings;
        truncated.value = res.truncated;
        declaredTotal.value = res.total;
      } catch (e) {
        console.error('Caricamento COD5 fallito:', e);
        bookings.value = [];
        truncated.value = false;
      } finally {
        loading.value = false;
      }
    };

    const loadPartner = async () => {
      try {
        const r = await partnerAPI.getPartnerDetail(route.params.id);
        partnerName.value = r.data?.company_name || '';
      } catch (e) {
        console.warn('Nome partner non disponibile:', e?.message);
      }
    };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    const euro = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

    const fmtDate = (d) =>
      d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

    const shortId = (id) => `#${String(id).slice(0, 8)}`;

    const openBooking = (id) => router.push(`/booking/detail/${id}`);
    const goBack = () => router.push('/contabilita');

    onMounted(() => { load(); loadPartner(); });

    return {
      year, month, loading, search, segment, truncated, declaredTotal,
      rowsCount, partnerName, transactions, filtered, filteredTotal, kpi,
      periodLabel, SEGMENTS,
      countOf, shiftMonth, euro, fmtDate, shortId, openBooking, goBack,
    };
  },
};
</script>

<style scoped>
.c5 { padding: 4px 0 32px; }
.c5__sp { flex: 1; }

.c5__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c5__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c5__back:hover { border-color: var(--brand-violet); }
.c5__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c5__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: var(--state-muted);
  padding: 3px 8px; border-radius: 6px;
}
.c5__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c5__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.warnbox {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 13px; margin-bottom: 14px;
  border-radius: 9px; font-size: 12.5px; line-height: 1.5;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}
.warnbox__ico { flex: none; font-size: 14px; line-height: 1.35; }
.warnbox__txt { flex: 1; }

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.cli__nm { font-weight: 600; color: var(--text-primary); }
.cli__sub { color: var(--text-tertiary); font-size: 11.5px; }
.c5__meta { margin-top: 3px; font-size: 11px; color: var(--text-tertiary); }
.val { font-variant-numeric: tabular-nums; font-weight: 700; color: var(--text-primary); }
.lnk {
  background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit;
  color: var(--brand-violet); font-weight: 600; font-size: 12.5px;
}
.lnk:hover { text-decoration: underline; }

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.c5__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

.c5 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
